<template>
  <AdminLayout>
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <RouterLink to="/admin/blog" class="back-btn">
          <i class="ti ti-arrow-left"></i>
        </RouterLink>
        <h2 class="text-white mb-0" style="font-weight:800">
          {{ isNew ? 'New Post' : 'Edit Post' }}
        </h2>
      </div>
      <div class="d-flex gap-2">
        <button class="btn" :disabled="saving" @click="save('draft')">
          <span class="border-style">{{ saving === 'draft' ? 'Saving...' : 'Save Draft' }}</span>
        </button>
        <button class="btn btn-publish" :disabled="saving" @click="save('published')">
          <span class="border-style">{{ saving === 'published' ? 'Publishing...' : 'Publish' }}</span>
        </button>
      </div>
    </div>

    <div v-if="fetchLoading" class="text-silver">Loading post...</div>

    <div v-else class="editor-layout">
      <!-- Main content -->
      <div class="editor-main">
        <!-- Title -->
        <div class="editor-field">
          <label>Title *</label>
          <input
            v-model="form.title"
            class="editor-input"
            placeholder="Enter post title..."
            @input="autoSlug"
          />
        </div>

        <!-- Slug -->
        <div class="editor-field">
          <label>Slug</label>
          <input v-model="form.slug" class="editor-input slug-input" placeholder="post-url-slug" />
        </div>

        <!-- Excerpt -->
        <div class="editor-field">
          <label>Excerpt <span class="label-hint">— short summary shown on listing page</span></label>
          <textarea v-model="form.excerpt" class="editor-input" rows="2" placeholder="Brief description of the post..."></textarea>
        </div>

        <!-- Content editor -->
        <div class="editor-field">
          <div class="editor-tabs">
            <button
              :class="['tab-btn', { active: activeTab === 'html' }]"
              @click="activeTab = 'html'"
            >
              <i class="ti ti-code"></i> HTML Source
            </button>
            <button
              :class="['tab-btn', { active: activeTab === 'preview' }]"
              @click="activeTab = 'preview'"
            >
              <i class="ti ti-eye"></i> Preview
            </button>
          </div>

          <!-- HTML toolbar -->
          <div v-show="activeTab === 'html'" class="html-toolbar">
            <button class="tool-btn" title="Bold" @click="insertTag('strong')"><b>B</b></button>
            <button class="tool-btn" title="Italic" @click="insertTag('em')"><i>I</i></button>
            <button class="tool-btn" title="Heading 2" @click="insertBlock('h2')">H2</button>
            <button class="tool-btn" title="Heading 3" @click="insertBlock('h3')">H3</button>
            <button class="tool-btn" title="Paragraph" @click="insertBlock('p')">P</button>
            <button class="tool-btn" title="Bullet list" @click="insertList()"><i class="ti ti-list"></i></button>
            <button class="tool-btn" title="Blockquote" @click="insertBlock('blockquote')">"</button>
            <button class="tool-btn" title="Link" @click="insertLink()"><i class="ti ti-link"></i></button>
            <div class="tool-sep"></div>
            <button class="tool-btn" title="Upload & insert image" @click="triggerImageUpload()">
              <i class="ti ti-photo"></i> Image
              <input ref="imageInput" type="file" accept="image/*" style="display:none" @change="onImageUpload" />
            </button>
            <button class="tool-btn" title="Embed YouTube video" @click="insertVideo()">
              <i class="ti ti-brand-youtube"></i> Video
            </button>
          </div>

          <!-- Inline URL bar (replaces browser prompt) -->
          <div v-show="activeTab === 'html' && urlPrompt.open" class="url-prompt-bar">
            <i :class="urlPrompt.mode === 'video' ? 'ti ti-brand-youtube' : 'ti ti-link'"></i>
            <input
              ref="urlPromptInput"
              v-model="urlPrompt.value"
              class="editor-input"
              :placeholder="urlPrompt.mode === 'video' ? 'https://www.youtube.com/watch?v=...' : 'https://...'"
              @keydown.enter.prevent="confirmUrlPrompt"
              @keydown.esc="urlPrompt.open = false"
            />
            <button class="tool-btn" @click="confirmUrlPrompt">Insert</button>
            <button class="tool-btn" @click="urlPrompt.open = false">Cancel</button>
            <span v-if="urlPrompt.error" class="url-prompt-error">{{ urlPrompt.error }}</span>
          </div>

          <!-- Textarea -->
          <textarea
            v-show="activeTab === 'html'"
            ref="contentArea"
            v-model="form.content"
            class="editor-input content-area"
            rows="24"
            placeholder="Paste HTML content here, or type and use the toolbar above..."
            spellcheck="false"
          ></textarea>

          <!-- Preview -->
          <div
            v-show="activeTab === 'preview'"
            class="blog-content content-preview"
            v-html="renderedPreview"
          ></div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="editor-sidebar">
        <!-- Cover image -->
        <div class="sidebar-card">
          <h5 class="sidebar-card-title">Cover Image</h5>
          <div v-if="form.cover_image" class="cover-preview">
            <img :src="form.cover_image" alt="cover" />
            <button class="remove-cover" @click="form.cover_image = ''">
              <i class="ti ti-x"></i>
            </button>
          </div>
          <button class="upload-btn" @click="triggerCoverUpload()">
            <i class="ti ti-upload"></i>
            {{ coverUploading ? 'Uploading...' : 'Upload Cover Image' }}
            <input ref="coverInput" type="file" accept="image/*" style="display:none" @change="onCoverUpload" />
          </button>
          <div class="editor-field mt-2">
            <label>Or enter image URL</label>
            <input v-model="form.cover_image" class="editor-input" placeholder="https://..." />
          </div>
        </div>

        <!-- SEO -->
        <div class="sidebar-card">
          <h5 class="sidebar-card-title">SEO</h5>
          <div class="editor-field">
            <label>Meta Title</label>
            <input v-model="form.meta_title" class="editor-input" placeholder="Page title for Google..." />
            <span class="char-count" :class="{ warn: (form.meta_title || '').length > 60 }">
              {{ (form.meta_title || '').length }}/60
            </span>
          </div>
          <div class="editor-field">
            <label>Meta Description</label>
            <textarea v-model="form.meta_description" class="editor-input" rows="3" placeholder="Description shown in search results..."></textarea>
            <span class="char-count" :class="{ warn: (form.meta_description || '').length > 160 }">
              {{ (form.meta_description || '').length }}/160
            </span>
          </div>
          <div class="editor-field">
            <label>Keywords</label>
            <input v-model="form.meta_keywords" class="editor-input" placeholder="keyword1, keyword2, ..." />
          </div>
        </div>

        <!-- Status -->
        <div class="sidebar-card">
          <h5 class="sidebar-card-title">Status</h5>
          <div class="status-display">
            <span :class="['status-badge', form.status]">{{ form.status }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="saveError" class="save-error mt-3">{{ saveError }}</div>
  </AdminLayout>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import AdminLayout from "@/components/admin/AdminLayout.vue";
import api from "@/services/api";
import { renderPostContent } from "@/utils/blogContent";

function slugify(str: string) {
  return str.toLowerCase().trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getYoutubeId(url: string) {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return m ? m[1] : null;
}

export default defineComponent({
  name: "BlogEditorPage",
  components: { AdminLayout },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isNew = computed(() => route.params.id === "new");

    const fetchLoading = ref(false);
    const saving = ref<string | false>(false);
    const saveError = ref("");
    const activeTab = ref<"html" | "preview">("html");
    const coverUploading = ref(false);

    const contentArea = ref<HTMLTextAreaElement | null>(null);
    const imageInput = ref<HTMLInputElement | null>(null);
    const coverInput = ref<HTMLInputElement | null>(null);

    const form = ref({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      cover_image: "",
      meta_title: "",
      meta_description: "",
      meta_keywords: "",
      status: "draft",
    });

    const renderedPreview = computed(() =>
      renderPostContent(form.value.content) || "<p style='color:#666'>Nothing to preview yet.</p>"
    );

    let slugTouched = false;

    function autoSlug() {
      if (!slugTouched) {
        form.value.slug = slugify(form.value.title);
      }
    }

    // Toolbar helpers
    function getSelection(): { start: number; end: number; text: string } {
      const el = contentArea.value;
      if (!el) return { start: 0, end: 0, text: "" };
      return { start: el.selectionStart, end: el.selectionEnd, text: el.value.slice(el.selectionStart, el.selectionEnd) };
    }

    function replaceSelection(replacement: string) {
      const el = contentArea.value;
      if (!el) return;
      const { start, end } = getSelection();
      form.value.content = el.value.slice(0, start) + replacement + el.value.slice(end);
      setTimeout(() => {
        el.focus();
        const pos = start + replacement.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    }

    function insertTag(tag: string) {
      const { text } = getSelection();
      replaceSelection(`<${tag}>${text || `text`}</${tag}>`);
    }

    function insertBlock(tag: string) {
      const { text } = getSelection();
      replaceSelection(`\n<${tag}>${text || `Content here`}</${tag}>\n`);
    }

    function insertList() {
      replaceSelection(`\n<ul>\n  <li>Item one</li>\n  <li>Item two</li>\n  <li>Item three</li>\n</ul>\n`);
    }

    // Inline URL bar — prompt()/alert() are not available in all environments
    const urlPrompt = reactive({
      open: false,
      mode: "link" as "link" | "video",
      value: "",
      error: "",
      start: 0, end: 0, text: "",
    });
    const urlPromptInput = ref<HTMLInputElement | null>(null);

    function openUrlPrompt(mode: "link" | "video") {
      const { start, end, text } = getSelection();
      urlPrompt.mode = mode;
      urlPrompt.value = "";
      urlPrompt.error = "";
      urlPrompt.start = start;
      urlPrompt.end = end;
      urlPrompt.text = text;
      urlPrompt.open = true;
      nextTick(() => urlPromptInput.value?.focus());
    }

    function insertLink() { openUrlPrompt("link"); }
    function insertVideo() { openUrlPrompt("video"); }

    function confirmUrlPrompt() {
      const url = urlPrompt.value.trim();
      if (!url) { urlPrompt.error = "Enter a URL."; return; }

      let replacement: string;
      if (urlPrompt.mode === "video") {
        const id = getYoutubeId(url);
        if (!id) { urlPrompt.error = "Invalid YouTube URL."; return; }
        replacement = `\n<div class="blog-video-wrap"><iframe src="https://www.youtube.com/embed/${id}" frameborder="0" allowfullscreen></iframe></div>\n`;
      } else {
        replacement = `<a href="${url}" target="_blank" rel="noopener">${urlPrompt.text || url}</a>`;
      }

      // Restore the selection captured before the input stole focus
      const el = contentArea.value;
      if (!el) return;
      form.value.content = el.value.slice(0, urlPrompt.start) + replacement + el.value.slice(urlPrompt.end);
      urlPrompt.open = false;
      setTimeout(() => {
        el.focus();
        const pos = urlPrompt.start + replacement.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    }

    function triggerImageUpload() { imageInput.value?.click(); }
    function triggerCoverUpload() { coverInput.value?.click(); }

    async function uploadFile(file: File): Promise<string> {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    }

    async function onImageUpload(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const url = await uploadFile(file);
      replaceSelection(`\n<img src="${url}" alt="" />\n`);
      (e.target as HTMLInputElement).value = "";
    }

    async function onCoverUpload(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      coverUploading.value = true;
      try {
        form.value.cover_image = await uploadFile(file);
      } finally {
        coverUploading.value = false;
        (e.target as HTMLInputElement).value = "";
      }
    }

    async function save(status: string) {
      if (!form.value.title.trim()) { saveError.value = "Title is required."; return; }
      if (!form.value.content.trim()) { saveError.value = "Content is required."; return; }
      saveError.value = "";
      saving.value = status;
      try {
        const payload = { ...form.value, status };
        if (isNew.value) {
          const { data } = await api.post("/api/admin/blog", payload);
          router.replace(`/admin/blog/${data.data.id}`);
        } else {
          await api.put(`/api/admin/blog/${route.params.id}`, payload);
        }
        form.value.status = status;
      } catch (err: any) {
        saveError.value = err?.response?.data?.message || "Failed to save.";
      } finally {
        saving.value = false;
      }
    }

    onMounted(async () => {
      if (!isNew.value) {
        fetchLoading.value = true;
        try {
          const { data } = await api.get(`/api/admin/blog?page=1`);
          const all = data.data as any[];
          const found = all.find((p) => p.id == route.params.id);
          if (found) {
            const detail = await api.get(`/api/v1/blog/${found.slug}`).catch(() => null);
            if (detail) Object.assign(form.value, detail.data.data);
            else Object.assign(form.value, found);
            slugTouched = true;
          }
        } finally {
          fetchLoading.value = false;
        }
      }
    });

    return {
      isNew, fetchLoading, saving, saveError, activeTab, coverUploading,
      contentArea, imageInput, coverInput, form, renderedPreview,
      autoSlug, insertTag, insertBlock, insertList, insertLink, insertVideo,
      urlPrompt, urlPromptInput, confirmUrlPrompt,
      triggerImageUpload, triggerCoverUpload, onImageUpload, onCoverUpload, save,
    };
  },
});
</script>

<style scoped>
.back-btn {
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 8px;
  background: #201516; border: 1px solid #3a2526;
  color: #c0c0c0; text-decoration: none; font-size: 1.1rem;
  transition: all 0.2s;
}
.back-btn:hover { background: #2b1e1f; color: #fff; }

.btn-publish span { background: linear-gradient(135deg, #c90e0e, #ff4444); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }

.editor-layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1.5rem;
  align-items: start;
}
@media (max-width: 900px) { .editor-layout { grid-template-columns: 1fr; } }

.editor-main { display: flex; flex-direction: column; gap: 0; }
.editor-field { margin-bottom: 1.25rem; }
.editor-field label { display: block; color: #a0a0a0; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; }
.label-hint { font-size: 0.75rem; text-transform: none; color: #666; letter-spacing: 0; }

.editor-input {
  width: 100%; background: #1a0f10; border: 1px solid #3a2526;
  border-radius: 8px; color: #fff; padding: 0.65rem 0.85rem;
  font-size: 0.95rem; font-family: inherit; resize: vertical;
  transition: border-color 0.2s; box-sizing: border-box;
}
.editor-input:focus { outline: none; border-color: #c90e0e; }
.slug-input { font-family: monospace; font-size: 0.85rem; color: #a0a0a0; }

.editor-tabs {
  display: flex; border-bottom: 1px solid #2a1a1b; margin-bottom: 0;
}
.tab-btn {
  display: flex; align-items: center; gap: 0.35rem;
  padding: 0.55rem 1rem; background: transparent; border: none;
  color: #888; cursor: pointer; font-size: 0.85rem; font-family: inherit;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  transition: all 0.2s;
}
.tab-btn.active { color: #c90e0e; border-bottom-color: #c90e0e; }
.tab-btn:hover:not(.active) { color: #fff; }

.html-toolbar {
  display: flex; flex-wrap: wrap; gap: 0.3rem;
  background: #1a0f10; border: 1px solid #3a2526;
  border-top: none; padding: 0.5rem 0.75rem;
}
.tool-btn {
  display: flex; align-items: center; gap: 0.25rem;
  padding: 0.25rem 0.55rem; background: #201516;
  border: 1px solid #3a2526; border-radius: 5px;
  color: #c0c0c0; cursor: pointer; font-size: 0.8rem;
  font-family: inherit; transition: all 0.15s;
}
.tool-btn:hover { background: #2b1e1f; color: #fff; border-color: #c90e0e; }
.tool-sep { width: 1px; background: #3a2526; margin: 0 0.2rem; }

.url-prompt-bar {
  display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
  background: #1a0f10; border: 1px solid #3a2526;
  border-top: none; padding: 0.5rem 0.75rem;
}
.url-prompt-bar i { color: #c90e0e; font-size: 1rem; }
.url-prompt-bar .editor-input { flex: 1; min-width: 220px; padding: 0.3rem 0.6rem; font-size: 0.85rem; }
.url-prompt-error { color: #f87171; font-size: 0.8rem; }

.content-area {
  border-top: none; border-radius: 0 0 8px 8px;
  font-family: 'Courier New', monospace; font-size: 0.88rem;
  line-height: 1.6; min-height: 480px;
}
.content-preview {
  background: #1a0f10; border: 1px solid #3a2526;
  border-top: none; border-radius: 0 0 8px 8px;
  padding: 1.5rem; min-height: 480px;
}

/* Sidebar */
.editor-sidebar { display: flex; flex-direction: column; gap: 1rem; }
.sidebar-card {
  background: #140d0e; border: 1px solid #2a1a1b;
  border-radius: 12px; padding: 1.25rem;
}
.sidebar-card-title { color: #fff; font-size: 0.88rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 1rem; }

.cover-preview { position: relative; margin-bottom: 0.75rem; border-radius: 8px; overflow: hidden; }
.cover-preview img { width: 100%; height: 140px; object-fit: cover; display: block; }
.remove-cover {
  position: absolute; top: 6px; right: 6px;
  background: rgba(0,0,0,0.7); border: none; border-radius: 50%;
  width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
  color: #fff; cursor: pointer; font-size: 0.8rem;
}
.upload-btn {
  display: flex; align-items: center; gap: 0.4rem;
  width: 100%; padding: 0.6rem; background: #201516;
  border: 1px dashed #3a2526; border-radius: 8px;
  color: #a0a0a0; cursor: pointer; font-size: 0.85rem;
  font-family: inherit; justify-content: center; transition: all 0.2s;
}
.upload-btn:hover { border-color: #c90e0e; color: #fff; }

.char-count { display: block; text-align: right; font-size: 0.72rem; color: #666; margin-top: 0.25rem; }
.char-count.warn { color: #fbbf24; }

.status-display { display: flex; align-items: center; }
.status-badge {
  display: inline-block; padding: 0.25rem 0.75rem;
  border-radius: 20px; font-size: 0.8rem; font-weight: 600; text-transform: capitalize;
}
.status-badge.published { background: rgba(74,222,128,0.15); color: #4ade80; }
.status-badge.draft { background: rgba(251,191,36,0.15); color: #fbbf24; }

.save-error { color: #ff6b6b; font-size: 0.9rem; background: rgba(201,14,14,0.1); border: 1px solid rgba(201,14,14,0.3); padding: 0.65rem 1rem; border-radius: 8px; }
</style>
